/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { isEmpty, Theme, useTheme, VFlow } from 'bold-ui'
import { RadioOption, resolveName, SimNaoEnum } from 'components/form'
import { RadioGroupBoxField } from 'components/form/field/RadioGroupBoxField'
import { SimNaoRadioGroupBoxField } from 'components/form/field/SimNaoRadioGroupBoxField'
import { SimNaoRadioListField } from 'components/form/field/SimNaoRadioListField'
import { SimNaoSwitchButtonField } from 'components/form/field/SimNaoSwitchButtonField'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useField } from 'react-final-form'
import { MetaPath } from 'util/metaPath'

import { ConsultaSeguimentoArrowDown } from '../../components/ConsultaSeguimentoArrowDown'
import { ConsultaSeguimentoIndicatorHelper } from '../../components/ConsultaSeguimentoIndicatorHelper'
import { ConsultaSeguimentoIndicatorResultado } from '../../components/ConsultaSeguimentoIndicatorResultado'
import { ConsultaSeguimentoIndicatorStart } from '../../components/ConsultaSeguimentoIndicatorStart'
import { ConsultaSeguimentoIndicatorStep } from '../../components/ConsultaSeguimentoIndicatorStep'
import { ConsultaSeguimentoPerguntaHeader } from '../../components/ConsultaSeguimentoPerguntaHeader'
import { useConsultaSeguimentoSetResultado } from '../../hooks/useConsultaSeguimentoSetResultado'
import { PassouFalhouEnum } from '../../model-consultaSeguimento'
import { hasAnySim } from '../../utils-consultaSeguimento'
import {
  ConsultaSeguimentoPergunta07ApontaPorEnum,
  ConsultaSeguimentoPergunta07Model,
  ConsultaSeguimentoPergunta07OlharParaEnum,
} from './model-consultaSeguimentoPergunta07'
import { isPergunta07OlharParaAllNao } from './utils-consultaSeguimentoPergunta07'

interface ConsultaSeguimentoPergunta07FieldProps {
  name: MetaPath<ConsultaSeguimentoPergunta07Model>
  perguntasParaAplicar: MChatPerguntaEnum[]
}

export function ConsultaSeguimentoPergunta07Field(props: Readonly<ConsultaSeguimentoPergunta07FieldProps>) {
  const { name, perguntasParaAplicar } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value },
  } = useField<ConsultaSeguimentoPergunta07Model>(resolveName(name), {
    subscription: { value: true },
  })

  useConsultaSeguimentoSetResultado<ConsultaSeguimentoPergunta07Model>({
    name,
    conditionToCalculate,
    calculateResultado,
  })

  const isPergunta6Included = perguntasParaAplicar.includes(MChatPerguntaEnum.PERGUNTA_06)

  return (
    <VFlow vSpacing={0}>
      <VFlow>
        <ConsultaSeguimentoPerguntaHeader
          pergunta={MChatPerguntaEnum.PERGUNTA_07}
          title={
            isPergunta6Included
              ? '“Acabamos de falar sobre o apontar para pedir algo. Mas, e para mostrar algo interessante para você, ele aponta com o dedo?”'
              : '“O seu filho, para mostrar algo interessante para você, ele aponta com o dedo?”'
          }
          description={
            isPergunta6Included && 'Assim que o entrevistador finalizar o item 6,\n deverá PERGUNTAR A TODOS:'
          }
          status={value.status}
          field={<SimNaoSwitchButtonField name={name.PERGUNTA_07_HEADER} />}
        />
        {!isEmpty(value.PERGUNTA_07_HEADER) && (
          <VFlow>
            <VFlow vSpacing={0}>
              <ConsultaSeguimentoIndicatorStart />
              {value.PERGUNTA_07_HEADER === SimNaoEnum.SIM && (
                <ConsultaSeguimentoIndicatorHelper
                  title='“Por favor, me dê um exemplo de algo que ele possa apontar para mostrar para você.”'
                  subtitle='(Se o genitor não der nenhum dos exemplos abaixo, perguntar estes que se seguem:)'
                />
              )}
            </VFlow>
            <VFlow vSpacing={0}>
              <SimNaoRadioListField<ConsultaSeguimentoPergunta07OlharParaEnum>
                name={name.PERGUNTA_07_OLHAR_PARA}
                title='Seu filho alguma vez já quis que você olhasse para algo interessante como...'
                description='Responda os exemplos até pontuar na Pergunta 7 ou avançar para o próximo fluxo'
                options={PERGUNTA_07_OLHAR_PARA_OPTIONS}
                required
              />
              {hasAnySim(value.PERGUNTA_07_OLHAR_PARA) && (
                <VFlow vSpacing={0}>
                  <ConsultaSeguimentoIndicatorStep text='Sim para qualquer um:' />
                  <SimNaoRadioGroupBoxField
                    name={name.PERGUNTA_07_APONTA_COM_DEDO}
                    label='“Como é que seu filho chama sua atenção para ele? Ele aponta com o dedo?”'
                    required
                  />
                  {value.PERGUNTA_07_APONTA_COM_DEDO === SimNaoEnum.SIM && (
                    <VFlow vSpacing={0} style={styles.apontaComDedo}>
                      <ConsultaSeguimentoArrowDown />
                      <RadioGroupBoxField<ConsultaSeguimentoPergunta07ApontaPorEnum>
                        name={name.PERGUNTA_07_APONTA_POR}
                        label='“Ele aponta para mostrar o interesse dele/dela ou para pedir ajuda?”'
                        options={PERGUNTA_07_APONTA_POR_OPTIONS}
                        labelStyle={styles.apontaComDedoLabel}
                        required
                      />
                    </VFlow>
                  )}
                </VFlow>
              )}
            </VFlow>
          </VFlow>
        )}
      </VFlow>
      {value.resultado && (
        <ConsultaSeguimentoIndicatorResultado
          text={{ FALHOU: isEmpty(value?.PERGUNTA_07_OLHAR_PARA) && 'Não para todos:' }}
          resultado={value.resultado}
        />
      )}
    </VFlow>
  )
}

const PERGUNTA_07_OLHAR_PARA_OPTIONS: RadioOption<ConsultaSeguimentoPergunta07OlharParaEnum>[] = [
  {
    label: 'Um avião no céu?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.AVIAO,
  },
  {
    label: 'Um caminhão na rua?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.CAMINHAO,
  },
  {
    label: 'Um bicho no chão?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.BICHO,
  },
  {
    label: 'Um animal no quintal?',
    value: ConsultaSeguimentoPergunta07OlharParaEnum.ANIMAL,
  },
]

const PERGUNTA_07_APONTA_POR_OPTIONS: RadioOption<ConsultaSeguimentoPergunta07ApontaPorEnum>[] = [
  {
    label: 'Para conseguir ajuda',
    value: ConsultaSeguimentoPergunta07ApontaPorEnum.AJUDA,
  },
  {
    label: 'Tanto para mostrar o interesse dele/dela como para conseguir ajuda',
    value: ConsultaSeguimentoPergunta07ApontaPorEnum.INTERESSE,
  },
]

const conditionToCalculate = (values: ConsultaSeguimentoPergunta07Model): boolean => {
  if (isPergunta07OlharParaAllNao(values.PERGUNTA_07_OLHAR_PARA)) {
    return true
  } else if (hasAnySim(values.PERGUNTA_07_OLHAR_PARA)) {
    if (values.PERGUNTA_07_APONTA_COM_DEDO === SimNaoEnum.SIM) {
      return !isEmpty(values.PERGUNTA_07_APONTA_POR)
    } else {
      return !isEmpty(values.PERGUNTA_07_APONTA_COM_DEDO)
    }
  }
  return false
}

const calculateResultado = (values: ConsultaSeguimentoPergunta07Model): PassouFalhouEnum => {
  if (isPergunta07OlharParaAllNao(values.PERGUNTA_07_OLHAR_PARA)) {
    return PassouFalhouEnum.FALHOU
  } else if (values.PERGUNTA_07_APONTA_POR === ConsultaSeguimentoPergunta07ApontaPorEnum.INTERESSE) {
    return PassouFalhouEnum.PASSOU
  } else {
    return PassouFalhouEnum.FALHOU
  }
}

const createStyles = (theme: Theme) => ({
  apontaComDedo: css`
    & > div:first-of-type {
      align-self: center;
    }
  `,
  apontaComDedoLabel: css`
    color: ${theme.pallete.primary.main};
  `,
})
